/**
 * state.ext.control from the hub (kept live by SSE 'ext'), or demo data in mock mode, and
 * the two changes: turn control on or off for a machine, send a message into a session.
 */
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import type { ControlMachineState, ControlResult, ControlState, ControlTerminalFirst, Session } from '@orgus/protocol';
import { CONTROL_DEFAULT_WAIT_SECONDS, CONTROL_INSTALL_FORMAT } from '@orgus/protocol';
import { apiPost } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';

export interface ControlView {
  /** loading: no state yet. missing: the hub has no control module. */
  phase: 'loading' | 'error' | 'missing' | 'ready';
  state: ControlState | undefined;
  mock: boolean;
}

function isControlState(v: unknown): v is ControlState {
  return !!v && typeof v === 'object' && !!(v as ControlState).machines && typeof (v as ControlState).machines === 'object';
}

/** Demo data: the first machine controlled, the second off, the third on an old Claude Code. */
export function mockControl(machines: { id: string; online: boolean }[], sessions: Session[], now: number): ControlState {
  const out: ControlState['machines'] = {};
  machines.forEach((m, i) => {
    const live = sessions.filter((s) => s.machineId === m.id && s.status !== 'ended');
    const installedAt = now - 50 * 60_000;
    const base: ControlMachineState = {
      machineId: m.id,
      online: m.online,
      installed: false,
      settingsFile: i === 1 ? '/Users/sam/.claude/settings.json' : 'C:\\Users\\sam\\.claude\\settings.json',
      support: { ok: true, version: '2.1.302' },
      port: null,
      listening: false,
      waitSeconds: CONTROL_DEFAULT_WAIT_SECONDS,
      warnings: [],
      at: now,
      sessions: live.map((s) => ({ sessionId: s.id, controlled: false, ...(s.pid ? { pid: s.pid } : {}), cwd: s.cwd, startedAt: s.startedAt })),
    };
    if (i === 0) {
      out[m.id] = {
        ...base,
        installed: true,
        installedAt,
        format: CONTROL_INSTALL_FORMAT,
        port: 51667,
        listening: m.online,
        sessions: base.sessions.map((x, j) => (j === live.length - 1 && live.length > 1 ? { ...x, why: 'started-before' as const } : { ...x, controlled: true, lastHookAt: now - 20_000 })),
      };
    } else if (i === 2) {
      out[m.id] = { ...base, support: { ok: false, version: '2.0.14', reason: 'Claude Code 2.0.14 has no http hooks (they need 2.1.63 or later). Hook commands would open a console window for every event on Windows, so control stays off. Update Claude Code, then turn it on.' } };
    } else {
      out[m.id] = base;
    }
  });
  return { machines: out };
}

function useMockSeed(): void {
  const { ready, mock, has } = useOrgus(useShallow((s) => ({ ready: s.phase === 'ready', mock: s.mode === 'mock', has: !!s.ext.control })));
  useEffect(() => {
    if (!mock || !ready || has) return;
    const st = useOrgus.getState();
    st.applyExt({ ext: 'control', type: 'state', data: mockControl(Object.values(st.machines), Object.values(st.sessions), Date.now()) });
  }, [mock, ready, has]);
}

export function useControl(): ControlView {
  useMockSeed();
  const { phase, mode, raw } = useOrgus(useShallow((s) => ({ phase: s.phase, mode: s.mode, raw: s.ext.control })));
  const mock = mode === 'mock';
  const state = isControlState(raw) ? raw : undefined;
  if (phase === 'loading') return { phase: 'loading', state, mock };
  if (phase === 'error' && !state) return { phase: 'error', state, mock };
  if (!state && !mock) return { phase: 'missing', state, mock };
  return { phase: 'ready', state, mock };
}

/** Just the state, for marks in lists and drawers. */
export function useControlState(): ControlState | undefined {
  // Demo mode seeds control here too, so marks and Send a message appear without visiting Settings first.
  useMockSeed();
  return useOrgus((s) => (isControlState(s.ext.control) ? s.ext.control : undefined));
}

function mockSet(machineId: string, on: boolean, waitSeconds?: number, terminalFirst?: ControlTerminalFirst): ControlResult {
  const st = useOrgus.getState();
  const cur = st.ext.control as ControlState | undefined;
  const m = cur?.machines[machineId];
  if (!cur || !m) return { ok: false, message: 'Demo data has no such machine.' };
  const next: ControlMachineState = on
    ? { ...m, installed: true, installedAt: m.installedAt ?? Date.now(), listening: true, port: m.port ?? 51667, waitSeconds: waitSeconds ?? m.waitSeconds, terminalFirst: terminalFirst ?? m.terminalFirst ?? 'terminal' }
    : { ...m, installed: false, listening: false, port: null, sessions: m.sessions.map((x) => ({ ...x, controlled: false })) };
  st.applyExt({ ext: 'control', type: 'state', data: { machines: { ...cur.machines, [machineId]: next } } });
  return { ok: true, message: on ? 'Session control is on (demo).' : 'Session control is off (demo).' };
}

export async function setControl(machineId: string, on: boolean, waitSeconds?: number, terminalFirst?: ControlTerminalFirst): Promise<ControlResult> {
  if (useOrgus.getState().mode === 'mock') return mockSet(machineId, on, waitSeconds, terminalFirst);
  const r = await apiPost<{ result: ControlResult; state: ControlState }>(`/api/control/${encodeURIComponent(machineId)}`, { on, ...(waitSeconds !== undefined ? { waitSeconds } : {}), ...(terminalFirst !== undefined ? { terminalFirst } : {}) });
  useOrgus.getState().applyExt({ ext: 'control', type: 'state', data: r.state });
  return r.result;
}

export async function sendSessionMessage(machineId: string, sessionId: string, text: string, attachmentIds: readonly string[] = []): Promise<ControlResult> {
  const files = attachmentIds.length ? ` with ${attachmentIds.length === 1 ? 'one file' : `${attachmentIds.length} files`}` : '';
  if (useOrgus.getState().mode === 'mock') return { ok: true, message: `Sent${files} (demo). Claude Code adds it as the session's next prompt.` };
  const r = await apiPost<{ result: ControlResult }>(`/api/control/${encodeURIComponent(machineId)}/message`, { sessionId, text, ...(attachmentIds.length ? { attachmentIds } : {}) });
  return r.result;
}
